import { createServerFn } from "@tanstack/react-start";
import { queryOptions } from "@tanstack/react-query";
import { getRouteApi } from "@tanstack/react-router";
import { dbError } from "./db";

export interface StoreSettings {
  nome_fantasia: string;
  razao_social: string | null;
  cnpj: string | null;
  cidade: string;
  uf: string;
  descricao: string;
  whatsapp_number: string;
  instagram_url: string;
  institutional_url: string;
  email_dpo: string | null;
  logo_url: string | null;
  favicon_url: string | null;
  email_contato: string | null;
  endereco: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cep: string | null;
}

// Usado como fallback enquanto a query carrega (e se ela falhar), para o
// rodapé/menu nunca aparecerem vazios — os mesmos valores reais do seed da
// migration `store_settings`.
export const DEFAULT_STORE_SETTINGS: StoreSettings = {
  nome_fantasia: "EG Elétrica",
  razao_social: null,
  cnpj: null,
  cidade: "Jaguariúna",
  uf: "SP",
  descricao:
    "Eletricista em Jaguariúna — instalações residenciais e comerciais, montagem de painéis elétricos e aterramento.",
  whatsapp_number: "5519997737657",
  instagram_url: "https://instagram.com/eg.eletrica10",
  institutional_url: "https://egeletrica.com.br",
  email_dpo: null,
  logo_url: null,
  favicon_url: null,
  email_contato: null,
  endereco: null,
  numero: null,
  complemento: null,
  bairro: null,
  cep: null,
};

// Extraída do handler: createServerFn lança "No Start context found" quando
// chamada fora de uma requisição real, então testar a const exportada
// diretamente quebra — mesmo motivo documentado em addresses.functions.ts.
export async function fetchStoreSettings(): Promise<StoreSettings> {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data, error } = await supabaseAdmin
    .from("store_settings")
    .select(
      "nome_fantasia, razao_social, cnpj, cidade, uf, descricao, whatsapp_number, instagram_url, institutional_url, email_dpo, logo_url, favicon_url, email_contato, endereco, numero, complemento, bairro, cep",
    )
    .eq("id", true)
    .single();
  if (error) throw dbError(error, "src/lib/settings.functions.ts");
  return data as StoreSettings;
}

export const getStoreSettings = createServerFn({ method: "GET" }).handler(() =>
  fetchStoreSettings(),
);

export const storeSettingsQuery = () =>
  queryOptions({
    queryKey: ["store-settings"],
    queryFn: () => getStoreSettings(),
    staleTime: 5 * 60 * 1000,
  });

// Lê o resultado do loader da rota raiz (serializado no HTML do SSR) em vez
// de uma query client-side: assim servidor e cliente renderizam exatamente o
// mesmo cabeçalho/rodapé no primeiro render, sem erro de hidratação.
const rootRoute = getRouteApi("__root__");

export function useStoreSettings(): StoreSettings {
  const data = rootRoute.useLoaderData() as StoreSettings | undefined;
  return data ?? DEFAULT_STORE_SETTINGS;
}

export function instagramHandle(url: string): string {
  return "@" + url.replace(/^https?:\/\/(www\.)?instagram\.com\//, "").replace(/\/$/, "");
}

export function displayDomain(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}
